import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MapPin, TrendingUp, Users, DollarSign, Award, BarChart3, ArrowLeft } from "lucide-react";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroStatTile } from "@/components/shared/PageHero";
import PageBreadcrumb from "@/components/shared/PageBreadcrumb";
import { AdSense, LeaderboardAd, InContentAd, SidebarAd, MobileBannerAd, ResponsiveDisplayAd, RectangleAd } from "@/components/shared/AdSense";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import RppToggle from "@/components/shared/RppToggle";
import AffiliateCta from "@/components/shared/AffiliateCta";
import StateHubLinks from "@/components/seo/StateHubLinks";
import { supabase } from "@/integrations/supabase/client";
import { getStateBySlug, getAreaCodeForLocation, formatLocationForDisplay } from "@/lib/stateUtils";

const StateOccupations = () => {
  const { state } = useParams<{ state: string }>();
  const navigate = useNavigate();
  const [useRpp, setUseRpp] = useState(false);

  const stateSlug = state || '';
  const stateInfo = getStateBySlug(stateSlug);
  const areaCode = getAreaCodeForLocation(stateSlug);
  const stateName = formatLocationForDisplay(stateSlug);
  const isInvalidState = !stateInfo || !areaCode;

  // Fetch top occupations data for the state
  const { data: occupationsData, isLoading } = useQuery({
    queryKey: ['state-occupations', areaCode, useRpp],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-top-occupations', {
        body: {
          area: stateName,
          type: 'high_paying',
          limit: 50,
          year: 2025,
          rpp: useRpp
        }
      });
      
      if (error) throw error;
      return data;
    },
    // Ensure we refetch when useRpp changes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: !isInvalidState
  });

  if (isInvalidState) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold mb-4">State Not Found</h1>
            <p className="text-muted-foreground mb-6">
              We couldn't find data for "{stateSlug}". Please check the state name and try again.
            </p>
            <Link to="/best-states">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Best States
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const summaryStats = occupationsData?.occupations ? (() => {
    const data = occupationsData.occupations;
    const salaries = data.map((occ: unknown) => useRpp ? (occ.real_annual_mean_wage || occ.annual_mean_wage || 0) : (occ.annual_mean_wage || 0)).filter(Boolean);
    const employment = data.map((occ: unknown) => occ.total_employment || 0);
    
    return {
      topOccupation: data[0],
      avgSalary: Math.round(salaries.reduce((sum: number, sal: number) => sum + sal, 0) / salaries.length),
      totalJobs: employment.reduce((sum: number, emp: number) => sum + emp, 0),
      occupationCount: data.length,
      salaryRange: {
        min: Math.min(...salaries),
        max: Math.max(...salaries)
      }
    };
  })() : null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const pageTitle = `Top Paying Jobs in ${stateName} - Best Occupations & Salaries`;
  const pageDescription = `Discover the highest paying jobs in ${stateName}. ${summaryStats?.topOccupation?.occ_title || 'Top occupations'} leads with ${formatCurrency(summaryStats?.topOccupation?.annual_mean_wage || 85000)} average salary.`;

  // SeoHead must render even while data is loading: prerender validation
  // rejects any snapshot whose <title> is still the default SPA shell.
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SeoHead title={pageTitle} description={pageDescription} location={stateName} />
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="h-32 bg-muted animate-pulse rounded" />
              <div className="h-96 bg-muted animate-pulse rounded" />
            </div>
            <div className="space-y-4">
              <div className="h-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        location={stateName}
      />

      {/* Mobile Banner Ad - Top */}
      <AdFrame className="block md:hidden mb-4">
        <MobileBannerAd key="mobile-top" />
      </AdFrame>

      {/* Hero Section */}
      <PageHero
        badge={{ icon: MapPin, label: 'State Employment Analysis' }}
        title={<>Top Paying Jobs in {stateName}</>}
        subtitle="Discover the highest paying occupations and career opportunities"
      >
        {/* Breadcrumb */}
        <PageBreadcrumb
          variant="onDark"
          className="mb-6"
          items={[
            { label: 'Home', href: '/' },
            { label: 'Best States', href: '/best-states' },
            { label: stateName },
          ]}
        />
        
        {/* Top ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <LeaderboardAd key="hero-leaderboard" className="max-w-4xl" />
        </AdFrame>

        {summaryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <HeroStatTile icon={Award} value={summaryStats.topOccupation?.occ_title?.substring(0, 20) || 'N/A'} label="Top Job" />
            <HeroStatTile icon={DollarSign} value={formatCurrency(summaryStats.avgSalary)} label="Avg Salary" />
            <HeroStatTile icon={Users} value={formatNumber(summaryStats.totalJobs)} label="Total Jobs" />
            <HeroStatTile icon={BarChart3} value={summaryStats.occupationCount} label="Occupations" />
          </div>
        )}
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Occupation Rankings */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-xl font-semibold">
                    Top Paying Occupations in {stateName}
                  </CardTitle>
                  <div className="flex items-center gap-4">
                    <RppToggle
                      enabled={useRpp}
                      onToggle={setUseRpp}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {useRpp ? 'Salaries adjusted for regional cost of living (purchasing power)' : 'Raw salary data without cost-of-living adjustments'}
                </p>
              </CardHeader>
              <CardContent>
                {occupationsData?.occupations && occupationsData.occupations.length > 0 ? (
                  <SalaryTable
                    firstColumnLabel="Occupation"
                    data={occupationsData.occupations.map((occ: unknown, index: number) => {
                      const annualMean = useRpp ? (occ.real_annual_mean_wage || occ.annual_mean_wage || 0) : (occ.annual_mean_wage || 0);
                      const annualMedian = useRpp ? (occ.real_annual_median_wage || occ.annual_median_wage || 0) : (occ.annual_median_wage || 0);
                      const hourlyMean = useRpp ? (occ.real_hourly_mean_wage || occ.hourly_mean_wage || (annualMean / 2080)) : (occ.hourly_mean_wage || (annualMean / 2080));
                      
                      return {
                        rank: index + 1,
                        occ_title: occ.occ_title || 'Unknown Occupation',
                        soc: occ.soc || '',
                        annual_mean_wage: annualMean,
                        annual_median_wage: annualMedian,
                        hourly_mean_wage: hourlyMean,
                        total_employment: occ.total_employment || 0,
                        jobs_per_1000: occ.jobs_per_1000 || 0,
                        mean_salary: annualMean,
                        median_salary: annualMedian,
                        hourly_mean: hourlyMean,
                        tot_emp: occ.total_employment || 0,
                        jobs_1000: occ.jobs_per_1000 || 0
                      };
                    })}
                    showAffiliateEvery={5}
                    area={stateName}
                    className="shadow-none border-0"
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No occupation data available for {stateName}.
                  </div>
                )}

                {/* Career opportunities callout */}
                <div className="mt-8 p-6 bg-gradient-to-r from-accent/10 to-accent/5 rounded-lg border border-accent/20">
                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-accent mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">Discover Career Opportunities</h3>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Explore salary trends and growth projections for {stateName}, then browse open roles. 
                        All of our salary data and tools are free to use.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Button 
                          className="bg-accent hover:bg-accent/90"
                          onClick={() => navigate('/calculators')}
                        >
                          Explore Salary Calculators
                        </Button>
                        <AffiliateCta
                          provider="indeed"
                          occupation="All Jobs"
                          location={stateName}
                          type="job-search"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* In-content ad */}
                <InContentAd key="table-content" />
              </CardContent>
            </Card>

            {/* Insights & Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>{stateName} Job Market Analysis</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Top Industries</h4>
                    {summaryStats ? (
                      <div className="space-y-3">
                        <p className="text-muted-foreground">
                          {summaryStats.topOccupation?.occ_title} leads the job market in {stateName} 
                          with {formatCurrency(summaryStats.topOccupation?.annual_mean_wage || 0)} average compensation.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                            <span>Strong local industry presence</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                            <span>Competitive compensation packages</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                            <span>Growing job opportunities</span>
                          </li>
                        </ul>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Market analysis will be displayed when data is available.</p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-wage-accent">Career Considerations</h4>
                    <p className="text-muted-foreground mb-4">
                      {stateName} offers diverse career opportunities across multiple industries. 
                      Consider these factors when evaluating job prospects in the state.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Cost of living considerations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Industry concentration</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Remote work availability</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Career advancement opportunities</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <p className="text-muted-foreground">
                  Salary data is based on {new Date().getFullYear()} Bureau of Labor Statistics data for {stateName}. 
                  Rankings reflect current market conditions and may vary by employer, experience level, and specific job requirements.
                </p>
              </CardContent>
            </Card>

            {/* Post-Analysis Responsive Ad */}
            <div className="my-6">
              <div className="flex justify-center">
                <div className="w-full max-w-md">
                  <AdSense 
                    key="post-analysis"
                    slot="2021378586" 
                    format="auto" 
                    className="min-h-[250px]"
                  />
                </div>
              </div>
            </div>

            {/* Bottom Content Ad */}
            <div className="my-6">
              <div className="flex justify-center">
                <AdSense 
                  key="bottom-content"
                  slot="8985994760" 
                  format="auto" 
                  className="min-h-[90px] w-full max-w-2xl"
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Sidebar Ad */}
            <SidebarAd key="primary-sidebar" />

            {/* Rectangle Ad - Sidebar (Alternative slot) */}
            <div className="hidden lg:block mt-6">
              <div className="flex justify-center">
                <AdSense 
                  key="sidebar-rectangle"
                  slot="8395215240" 
                  format="auto" 
                  className="min-h-[250px] w-full max-w-[300px]"
                />
              </div>
            </div>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {summaryStats && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Highest Salary</span>
                      <span className="font-semibold">{formatCurrency(summaryStats.salaryRange.max)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Average Salary</span>
                      <span className="font-semibold">{formatCurrency(summaryStats.avgSalary)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Employment</span>
                      <span className="font-semibold">{formatNumber(summaryStats.totalJobs)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Occupations Tracked</span>
                      <span className="font-semibold">{summaryStats.occupationCount}</span>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Second Rectangle Ad - Between sidebar sections (different implementation) */}
            <div className="hidden lg:block">
              <div className="bg-muted/30 rounded-lg p-4 flex justify-center">
                <AdSense 
                  key="sidebar-secondary"
                  slot="1498672391" 
                  format="auto" 
                  className="min-h-[200px] w-full max-w-[280px]"
                />
              </div>
            </div>

            {/* Related Links */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Explore More</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link 
                  to="/best-states" 
                  className="block p-3 rounded-lg bg-accent/10 hover:bg-accent/20 transition-colors"
                >
                  <div className="font-medium">Best States</div>
                  <div className="text-sm text-muted-foreground">Compare states by occupation</div>
                </Link>
                <Link 
                  to="/salary-calculator" 
                  className="block p-3 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors"
                >
                  <div className="font-medium">Salary Calculator</div>
                  <div className="text-sm text-muted-foreground">Calculate your worth</div>
                </Link>
                <Link 
                  to="/trends" 
                  className="block p-3 rounded-lg bg-secondary/10 hover:bg-secondary/20 transition-colors"
                >
                  <div className="font-medium">Salary Trends</div>
                  <div className="text-sm text-muted-foreground">Track market changes</div>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* SEO Internal Linking */}
        <div className="container mx-auto px-4 py-8">
          <StateHubLinks stateSlug={stateSlug} />
        </div>

        {/* Bottom Section Ads */}
        <div className="container mx-auto px-4 pb-8">
          {/* Desktop Leaderboard */}
          <AdFrame className="hidden md:block mb-6">
            <div className="flex justify-center">
              <LeaderboardAd key="bottom-leaderboard" />
            </div>
          </AdFrame>
          
          {/* Mobile Banner - Bottom */}
          <div className="block md:hidden">
            <div className="flex justify-center">
              <AdSense 
                key="mobile-bottom"
                slot="1498672391" 
                format="auto" 
                className="min-h-[50px] w-full"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateOccupations;